import React, { useState, useEffect } from 'react';
import { X, Calendar, FileText, IndianRupee } from 'lucide-react';
import Alert from '../common/Alert.js';
import billingPaymentService from '../../services/BillingPaymentService.js';

const EditPaymentModal = ({ isOpen, onClose, payment, billing, onPaymentUpdated }) => {
  const [formData, setFormData] = useState({
    payment_date: '',
    payment_mode: '',
    amount: '',
    bank_payment: '',
    tds: '',
    reference_no: '',
    remarks: ''
  });

  const [loading, setLoading] = useState(false);
  const [originalAmount, setOriginalAmount] = useState(0);

  useEffect(() => {
    if (payment && isOpen) {
      setFormData({
        payment_date: payment.payment_date || '',
        payment_mode: payment.payment_mode || 'cash',
        amount: payment.amount || '',
        bank_payment: payment.bank_payment ?? payment.amount ?? '',
        tds: payment.tds ?? 0,
        reference_no: payment.reference_no || '',
        remarks: payment.remarks || ''
      });
      setOriginalAmount(payment.amount || 0);
    }
  }, [payment, isOpen]);

  if (!isOpen || !payment || !billing) return null;

  const balance = (billing.total_amount || 0) - (billing.total_paid || 0);
  const maxAmount = Math.max(0, balance + originalAmount);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: ['amount', 'bank_payment', 'tds'].includes(name)
          ? parseFloat(value) || ''
          : value,
      };
      if (name === 'bank_payment') {
        const total = parseFloat(updated.amount) || 0;
        const bank = parseFloat(value) || 0;
        updated.tds = total > 0 ? parseFloat((total - bank).toFixed(2)) : '';
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || formData.amount <= 0) {
      Alert.error('Please enter a valid payment amount');
      return;
    }
    if (formData.amount > maxAmount) {
      Alert.error(`Payment amount cannot exceed ₹${maxAmount.toFixed(2)}`);
      return;
    }
    setLoading(true);
    try {
      const bankPay = parseFloat(formData.bank_payment) || 0;
      const tdsVal  = parseFloat(formData.tds) || 0;
      if (Math.round((bankPay + tdsVal) * 100) !== Math.round(parseFloat(formData.amount) * 100)) {
        Alert.error('Bank Payment + TDS must equal the total payment amount');
        setLoading(false);
        return;
      }
      await billingPaymentService.update(payment.id, {
        payment_date: formData.payment_date,
        payment_mode: formData.payment_mode,
        amount: formData.amount,
        bank_payment: bankPay,
        tds: tdsVal,
        reference_no: formData.reference_no || null,
        remarks: formData.remarks || null
      });
      Alert.success('Payment updated successfully!');
      onPaymentUpdated();
      handleClose();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update payment');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => { onClose(); };
  const handleBackdropClick = (e) => { if (e.target === e.currentTarget) handleClose(); };

  const remainingBalance = maxAmount - (formData.amount ? parseFloat(formData.amount) : 0);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <IndianRupee className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Edit Payment</h3>
              <p className="text-xs text-gray-500">Payment ID: {payment?.id}</p>
            </div>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Two-Column Body ── */}
        <div className="flex flex-col md:flex-row min-h-0">

          {/* LEFT COLUMN */}
          <div className="md:w-2/5 px-6 py-5 border-b md:border-b-0 md:border-r border-gray-200 flex flex-col gap-4">

            {/* Billing Summary Box */}
            <div className="border border-blue-200 rounded-lg overflow-hidden">
              <div className="bg-blue-50 px-4 py-2 border-b border-blue-200">
                <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Billing Summary</p>
              </div>
              <div className="divide-y divide-blue-100">
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-sm text-gray-600">Total Amount</span>
                  <span className="text-sm font-semibold text-gray-900">₹{(billing.total_amount || 0).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-sm text-gray-600">Paid (excl. this)</span>
                  <span className="text-sm font-semibold text-gray-900">
                    ₹{(billing.total_paid - originalAmount || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5 bg-blue-50">
                  <span className="text-sm font-medium text-gray-700">Max Edit Amount</span>
                  <span className="text-sm font-bold text-red-600">₹{maxAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Payment Amount Box */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Payment Amount</p>
              </div>
              <div className="px-4 py-3 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    <span className="flex items-center gap-1">
                      <IndianRupee className="w-3.5 h-3.5" />
                      Amount *&nbsp;<span className="font-normal text-gray-400">(Max: ₹{maxAmount.toFixed(2)})</span>
                    </span>
                  </label>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    min="0"
                    max={maxAmount}
                    placeholder="0.00"
                    required
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {formData.amount && remainingBalance >= 0 && (
                    <p className="text-xs text-gray-500 mt-1">Remaining: ₹{remainingBalance.toFixed(2)}</p>
                  )}
                  {formData.amount && remainingBalance < 0 && (
                    <p className="text-xs text-red-600 mt-1">Amount exceeds maximum allowed</p>
                  )}
                </div>

                {/* Bank Payment + TDS */}
                {formData.amount > 0 && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Bank Payment *</label>
                        <input
                          type="number"
                          name="bank_payment"
                          value={formData.bank_payment}
                          onChange={handleChange}
                          min="0"
                          max={formData.amount}
                          placeholder="0.00"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">TDS</label>
                        <input
                          type="number"
                          name="tds"
                          value={formData.tds}
                          onChange={handleChange}
                          min="0"
                          max={formData.amount}
                          placeholder="0.00"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    {formData.bank_payment !== '' && formData.tds !== '' && (
                      <div>
                        {Math.round(((parseFloat(formData.bank_payment) || 0) + (parseFloat(formData.tds) || 0)) * 100) === Math.round((parseFloat(formData.amount) || 0) * 100) ? (
                          <p className="text-xs text-green-600 font-medium">✓ Bank Payment + TDS = ₹{formData.amount}</p>
                        ) : (
                          <p className="text-xs text-red-600">
                            Sum = ₹{((parseFloat(formData.bank_payment) || 0) + (parseFloat(formData.tds) || 0)).toFixed(2)} (must = ₹{formData.amount})
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="md:w-3/5 px-6 py-5 flex flex-col gap-4">

            {/* Row 1: Date + Mode */}
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />Payment Date *</span>
                </label>
                <div className='relative w-full'>
                  <input
                                    type="date"
                                    name="payment_date"
                                    value={formData.payment_date}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                  />
                  <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Payment Mode *</label>
                <select
                  name="payment_mode"
                  value={formData.payment_mode}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                  <option value="neft">NEFT</option>
                  <option value="rtgs">RTGS</option>
                  <option value="upi">UPI</option>
                  <option value="dd">Demand Draft</option>
                  <option value="credit">Credit</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            {/* Row 2: Reference No + Remarks */}
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5" />Reference No</span>
                </label>
                <input
                  type="text"
                  name="reference_no"
                  value={formData.reference_no}
                  onChange={handleChange}
                  placeholder="Cheque #, Txn ID"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Remarks</label>
                <textarea
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  placeholder="Additional notes"
                  rows="1"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !formData.amount || parseFloat(formData.amount) <= 0 || parseFloat(formData.amount) > maxAmount}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <><span className="inline-block animate-spin">⏳</span>Updating...</>
            ) : (
              <><IndianRupee className="w-4 h-4" />Update Payment</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditPaymentModal;